import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import translationService from '../../services/translationService';
import styles from './Blog.module.css';
import { Link } from 'react-router-dom';

function Blog({ lang }) {
  const t = {
    en: {
      blogHeader: 'Our Blog',
      blogDesc: 'Stay updated with our latest news and insights',
      newest: 'Newest First',
      oldest: 'Oldest First',
      loading: 'Loading blog posts...',
      error: 'Failed to load blog posts',
      noBlogs: 'No blog posts available at the moment.',
      by: 'By',
      readMore: 'Read More',
    },
    ar: {
      blogHeader: 'مدونتنا',
      blogDesc: 'تابع آخر الأخبار والمستجدات لدينا',
      newest: 'الأحدث أولاً',
      oldest: 'الأقدم أولاً',
      loading: 'جاري تحميل المقالات...',
      error: 'فشل تحميل المقالات',
      noBlogs: 'لا توجد مقالات حالياً.',
      by: 'بواسطة',
      readMore: 'اقرأ المزيد',
    },
    tr: {
      blogHeader: 'Blogumuz',
      blogDesc: 'En son haberlerimiz ve güncellemelerimiz için takipte kalın',
      newest: 'En Yeni',
      oldest: 'En Eski',
      loading: 'Blog gönderileri yükleniyor...',
      error: 'Blog gönderileri yüklenemedi',
      noBlogs: 'Şu anda blog gönderisi yok.',
      by: 'Yazan',
      readMore: 'Devamını Oku',
    }
  }[lang];
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const blogsQuery = query(
          collection(db, 'blogs'),
          orderBy('createdAt', 'desc')
        );
        
        const blogsSnapshot = await getDocs(blogsQuery);
        const blogsData = blogsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
        }));
        
        setBlogs(blogsData);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setError(t.error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const getSortedBlogs = () => {
    if (!blogs.length) return [];
    
    return [...blogs].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return a.createdAt - b.createdAt;
        case 'newest':
        default:
          return b.createdAt - a.createdAt;
      }
    });
  };

  if (loading) {
    return (
      <div className={styles.blogPage}>
        <div className={styles.loading}>{t.loading}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.blogPage}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.blogPage}>
      <div className={styles.blogHeader}>
        <h1>{t.blogHeader}</h1>
        <p>{t.blogDesc}</p>
        
        <div className={styles.sortContainer}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="newest">{t.newest}</option>
            <option value="oldest">{t.oldest}</option>
          </select>
        </div>
      </div>

      <div className={styles.blogGrid}>
        {getSortedBlogs().map(blog => {
          // Get translated content based on language
          const getTranslatedContent = (field) => {
            return translationService.getTranslatedContent(blog, field, lang);
          };

          return (
            <article key={blog.id} className={styles.blogCard}>
              <div className={styles.blogImage}>
                <img src={blog.imageUrl} alt={getTranslatedContent('title')} />
              </div>
              <div className={styles.blogContent}>
                <h2>{getTranslatedContent('title')}</h2>
                <p className={styles.blogMeta}>
                  {t.by} {blog.author} • {blog.createdAt.toLocaleDateString()}
                </p>
                <p className={styles.blogSummary}>{getTranslatedContent('summary')}</p>
                <Link to={`/blog/${blog.id}`} className={styles.readMore}>
                  {t.readMore}
                </Link>
              </div>
            </article>
          );
        })}
      </div>

      {blogs.length === 0 && (
        <div className={styles.noBlogs}>{t.noBlogs}</div>
      )}
    </div>
  );
}

export default Blog; 